import z from "zod";
// src/authProviders/AuthProvider.ts
const AuthProvider = {
	GOOGLE: "google",
	END_TO_END: "end-to-end"
};
// ../../node_modules/.pnpm/@alextheman+utility@5.32.0_zod@4.6.7/node_modules/@alextheman/utility/dist/index.js
const FILE_PATH_REGEX_PATTERN = String.raw`(?<directory>.+)[\/\\](?<base>[^\/\\]+)`;
RegExp(`^${FILE_PATH_REGEX_PATTERN}$`);
new RegExp(`^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$`);
const VERSION_NUMBER_REGEX_PATTERN = String.raw`(?:v)?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)`;
const VERSION_NUMBER_REGEX = RegExp(`^${VERSION_NUMBER_REGEX_PATTERN}$`);
/**
* Represents errors that can be described using a standardised error code, and a human-readable error message.
*
* @category Types
*
* @template ErrorCode The type of the standardised error code.
*/
var CodeError = class CodeError extends Error {
	code;
	/**
	* @param code - A standardised code (e.g. UNEXPECTED_DATA).
	* @param message  - A human-readable error message (e.g. The data provided is invalid).
	* @param options - Extra options to pass to super Error constructor.
	*/
	constructor(code, message = "Something went wrong.", options) {
		super(message, options);
		if (Error.captureStackTrace) Error.captureStackTrace(this, new.target);
		this.name = new.target.name;
		this.code = code;
		Object.defineProperty(this, "message", { enumerable: true });
		Object.setPrototypeOf(this, new.target.prototype);
	}
	/**
	* Checks whether the given input may have been caused by a CodeError.
	*
	* @param input - The input to check.
	*
	* @returns `true` if the input is a CodeError, and `false` otherwise. The type of the input will also be narrowed down to CodeError if `true`.
	*/
	static check(input) {
		if (input instanceof CodeError) return true;
		return containsKeys(input, ["message", "code"]) && typeof input.message === "string" && typeof input.code === "string";
	}
	static checkCaughtError(error, options) {
		if (this.check(error)) {
			if (options?.expectedCode && error.code !== options.expectedCode) throw new Error(normaliseIndents`The error code on the thrown error does not match the expected error code.
            
            Expected: ${options.expectedCode}
            Received: ${error.code}
            `, { cause: error });
			return error;
		}
		throw error;
	}
	/**
	* Check a `CodeError` against its error code
	*
	* This will also automatically narrow down the type of the input to be `CodeError`, with its error code properly typed if this function returns true.
	*
	* @template ErrorCode The type of the error code
	*
	* @param input - The input to check.
	* @param code - The expected code of the resulting error.
	*
	* @returns `true` if the error code matches the expected code, and `false` otherwise. The type of the input will also be narrowed down to CodeError, and its code will be narrowed to the expected code's type if the function returns `true`.
	*/
	static checkWithCode(input, code) {
		return this.check(input) && input.code === code;
	}
	/**
	* Gets the thrown `CodeError` from a given function if one was thrown, and re-throws any other errors, or throws a default `CodeError` if no error thrown.
	*
	* @param errorFunction - The function expected to throw the error.
	* @param options - Extra options to apply.
	*
	* @throws {Error} Any other errors thrown by the `errorFunction` that are not a `CodeError`.
	* @throws {Error} If no `CodeError` was thrown by the `errorFunction`
	*
	* @returns The `CodeError` that was thrown by the `errorFunction`
	*/
	static expectError(errorFunction, options) {
		try {
			errorFunction();
		} catch (error) {
			return this.checkCaughtError(error, options);
		}
		throw new Error(`Expected a ${this.name} to be thrown but none was thrown`);
	}
	/**
	* Gets the thrown `CodeError` from a given asynchronous function if one was thrown, and re-throws any other errors, or throws a default `CodeError` if no error thrown.
	*
	* @param errorFunction - The function expected to throw the error.
	* @param options - Extra options to apply.
	*
	* @throws {Error} Any other errors thrown by the `errorFunction` that are not a `CodeError`.
	* @throws {Error} If no `CodeError` was thrown by the `errorFunction`
	*
	* @returns The `CodeError` that was thrown by the `errorFunction`
	*/
	static async expectErrorAsync(errorFunction, options) {
		try {
			await errorFunction();
		} catch (error) {
			return this.checkCaughtError(error, options);
		}
		throw new Error(`Expected a ${this.name} to be thrown but none was thrown`);
	}
	/**
	* Converts the `CodeError` instance to a serialised JSON payload.
	*
	* @returns A JSON serialised version of the current `CodeError` instance.
	*/
	toJSON() {
		return {
			code: this.code,
			message: this.message
		};
	}
};
/**
* Represents errors you may get that may've been caused by a specific piece of data.
*
* @category Types
*
* @template DataType The type of the data that caused the error.
* @template ErrorCode The type of the standardised error code.
*/
var DataError$1 = class DataError$1 extends CodeError {
	data;
	/**
	* @param data - The data that caused the error.
	* @param code - A standardised code (e.g. UNEXPECTED_DATA).
	* @param message - A human-readable error message (e.g. The data provided is invalid).
	* @param options - Extra options to pass to super Error constructor.
	*/
	constructor(data, code, message = "The data provided is invalid", options) {
		super(code, message, options);
		if (Error.captureStackTrace) Error.captureStackTrace(this, new.target);
		this.name = new.target.name;
		this.data = data;
		Object.defineProperty(this, "message", { enumerable: true });
		Object.setPrototypeOf(this, new.target.prototype);
	}
	/**
	* Checks whether the given input may have been caused by a DataError.
	*
	* @param input - The input to check.
	*
	* @returns `true` if the input is a DataError, and `false` otherwise. The type of the input will also be narrowed down to DataError if `true`.
	*/
	static check(input) {
		if (input instanceof DataError$1) return true;
		return containsKeys(input, [
			"data",
			"code",
			"message"
		]) && typeof input.message === "string" && typeof input.code === "string" && isNonNullableObject(input.data);
	}
	/**
	* Check a `DataError` against its error code
	*
	* This will also automatically narrow down the type of the input to be `DataError`, with its error code properly typed if this function returns true.
	*
	* @template ErrorCode The type of the error code
	*
	* @param input - The input to check.
	* @param code - The expected code of the resulting error.
	*
	* @returns `true` if the error code matches the expected code, and `false` otherwise. The type of the input will also be narrowed down to `DataError`, and its code will be narrowed to the expected code's type if the function returns `true`.
	*/
	static checkWithCode(input, code) {
		return this.check(input) && input.code === code;
	}
	/**
	* Gets the thrown `DataError` from a given function if one was thrown, and re-throws any other errors, or throws a default `DataError` if no error thrown.
	*
	* @param errorFunction - The function expected to throw the error.
	* @param options - Extra options to apply.
	*
	* @throws {Error} Any other errors thrown by the `errorFunction` that are not a `DataError`.
	* @throws {Error} If no `DataError` was thrown by the `errorFunction`
	*
	* @returns The `DataError` that was thrown by the `errorFunction`
	*/
	static expectError(errorFunction, options) {
		return super.expectError(errorFunction, options);
	}
	/**
	* Gets the thrown `DataError` from a given asynchronous function if one was thrown, and re-throws any other errors, or throws a default `DataError` if no error thrown.
	*
	* @param errorFunction - The function expected to throw the error.
	* @param options - Extra options to apply.
	*
	* @throws {Error} Any other errors thrown by the `errorFunction` that are not a `DataError`.
	* @throws {Error} If no `DataError` was thrown by the `errorFunction`
	*
	* @returns The `DataError` that was thrown by the `errorFunction`
	*/
	static async expectErrorAsync(errorFunction, options) {
		return await super.expectErrorAsync(errorFunction, options);
	}
	/**
	* Converts the `DataError` instance to a serialised JSON payload.
	*
	* @returns A JSON serialised version of the current `DataError` instance.
	*/
	toJSON() {
		return {
			...super.toJSON(),
			data: this.data
		};
	}
};
/**
* Creates an array of numbers within a given range.
*
* - The range is inclusive of `start` and exclusive of `stop`.
* - The sign of `step` must match the direction of the range.
*
* @category Array Helpers
*
* @param start - The number to start at (inclusive).
* @param stop - The number to stop at (exclusive).
* @param step - The step size between numbers, defaulting to 1.
*
* @throws {DataError} If `step` is `0`.
* @throws {DataError} If `step` direction does not match the order of `start` and `stop`.
*
* @returns An array of numbers satisfying the range provided.
*/
function range(start, stop, step = 1) {
	const numbers = [];
	if (step === 0) throw new DataError$1({ step }, "ZERO_STEP_SIZE", "Step size cannot be zero.");
	else if (step > 0) {
		if (start > stop) throw new DataError$1({
			start,
			stop,
			step
		}, "INVALID_BOUNDARIES", "The starting value cannot be bigger than the final value if step is positive");
		for (let i = start; i < stop; i += step) numbers.push(i);
	} else if (step < 0) {
		if (start < stop) throw new DataError$1({
			start,
			stop,
			step
		}, "INVALID_BOUNDARIES", "The final value cannot be bigger than the starting value if step is negative");
		for (let i = start; i > stop; i += step) numbers.push(i);
	}
	return numbers;
}
/**
* Determines if the given input is a non-nullable object, narrowing the type down as such if it is
*
* @category Type Assertions
*
* @param input - The input to check
*
* @returns `true` if the input is a non-nullable object, and `false` otherwise. The input type will also be narrowed down to be a non-nullable object.
*/
function isNonNullableObject(input) {
	return typeof input === "object" && input !== null;
}
/**
* Determines if the given object contains all of the keys provided.
*
* @category Type Assertions
*
* @param input - The input object to check.
* @param keys - The keys expected to be in the input object.
*
* @returns `true` if the input object contains all provided keys, and `false` otherwise. The input type will also be narrowed down to be a record with the provided keys with an unknown value type.
*/
function objectContainsKeys(input, keys) {
	const expectedKeys = Array.isArray(keys) ? keys : [keys];
	if (expectedKeys.length === 0) return false;
	for (const key of expectedKeys) if (!(key in input)) return false;
	return true;
}
/**
* Determines if the given input is a non-nullable object, and if that object contains all of the keys provided.
*
* @category Type Assertions
*
* @param input - The input to check.
* @param keys - The keys expected to be in the input if it's an object.
*
* @returns `true` if the input is an object containing all provided keys, and `false` otherwise. The input type will also be narrowed down to be a record with the provided keys, each with an unknown value type.
*/
function containsKeys(input, keys) {
	return isNonNullableObject(input) && objectContainsKeys(input, keys);
}
/**
* Creates a new array where each element is the result of the provided callback.
*
* If the callback returns at least one Promise, the entire result will be wrapped
* in a `Promise` and resolved with `Promise.all`. Otherwise, a plain array is returned.
*
* @category Array Helpers
*
* @template ItemType - The return type of the callback (awaited if any items are a Promise) that becomes the type of the array items.
*
* @param callback - A function invoked with the current index. May return a value or a Promise.
* @param length - The desired length of the resulting array.
* @param options - Extra options to apply if any item is asynchronous.
*
* @returns An array of the callback results, or a Promise resolving to one if the callback is async.
*/
function fillArray(callback, length = 1, options) {
	if (options?.sequential) return (async () => {
		const resolvedArray = [];
		for (const index of range(0, length)) resolvedArray.push(await callback(index));
		return resolvedArray;
	})();
	const outputArray = new Array(length).fill(null).map((_, index) => {
		return callback(index);
	});
	if (outputArray.some((item) => {
		return item instanceof Promise || containsKeys(item, "then") && typeof item.then === "function";
	})) return Promise.all(outputArray);
	return outputArray;
}
/**
* Creates a new array of tuples, each containing the item at the given index from both arrays.
*
* If `secondArray` is shorter than `firstArray`, the second position in the tuple
* will be `undefined`. Iteration always uses the length of the first array.
*
* @category Array Helpers
*
* @template FirstArrayItem
* @template SecondArrayItem
*
* @param firstArray - The first array. Each item in this will take up the first tuple spot.
* @param secondArray - The second array. Each item in this will take up the second tuple spot.
*
* @returns An array of `[firstItem, secondItem]` tuples for each index in `firstArray`.
*/
function paralleliseArrays(firstArray, secondArray) {
	const outputArray = [];
	for (let i = 0; i < firstArray.length; i++) outputArray.push([firstArray[i], secondArray[i]]);
	return outputArray;
}
/**
* Converts a string to an integer and throws an error if it cannot be converted.
*
* @category Parsers
*
* @param string - A string to convert into a number.
* @param radix - A value between 2 and 36 that specifies the base of the number in string. If this argument is not supplied, strings with a prefix of '0x' are considered hexadecimal. All other strings are considered decimal.
*
* @throws {DataError} If the provided string cannot safely be converted to an integer.
*
* @returns The integer parsed from the input string.
*/
function parseIntStrict(string, radix) {
	const trimmedString = string.trim();
	const maxAllowedAlphabeticalCharacter = radix && radix > 10 && radix <= 36 ? String.fromCharCode(87 + radix - 1) : void 0;
	if (!(radix && radix > 10 && radix <= 36 ? new RegExp(`^[+-]?[0-9a-${maxAllowedAlphabeticalCharacter}]+$`, "i") : /^[+-]?\d+$/).test(trimmedString)) throw new DataError$1(radix ? {
		string,
		radix
	} : { string }, "INTEGER_PARSING_ERROR", `Only numeric values${radix && radix > 10 && radix <= 36 ? ` or character${radix !== 11 ? "s" : ""} A${radix !== 11 ? `-${maxAllowedAlphabeticalCharacter?.toUpperCase()} ` : " "}` : " "}are allowed.`);
	if (radix && radix < 10 && [...trimmedString.replace(/^[+-]/, "")].some((character) => {
		return parseInt(character) >= radix;
	})) throw new DataError$1({
		string,
		radix
	}, "INTEGER_PARSING_ERROR", "Value contains one or more digits outside of the range of the given radix.");
	const parseIntResult = parseInt(trimmedString, radix);
	if (isNaN(parseIntResult)) throw new DataError$1({ string }, "INTEGER_PARSING_ERROR", "Value is not a valid integer.");
	return parseIntResult;
}
/**
* Represents a software version number, considered to be made up of a major, minor, and patch part.
*
* @category Types
*/
var VersionNumber = class VersionNumber {
	static NON_NEGATIVE_TUPLE_ERROR = "Input array must be a tuple of three non-negative integers.";
	/** The major number. Increments when a feature is removed or changed in a way that is not backwards-compatible with the previous release. */
	major = 0;
	/** The minor number. Increments when a new feature is added/deprecated and is expected to be backwards-compatible with the previous release. */
	minor = 0;
	/** The patch number. Increments when the next release is fixing a bug or doing a small refactor that should not be noticeable in practice. */
	patch = 0;
	/**
	* @param input - The input to create a new instance of `VersionNumber` from.
	*/
	constructor(input) {
		if (input instanceof VersionNumber) {
			this.major = input.major;
			this.minor = input.minor;
			this.patch = input.patch;
		} else if (typeof input === "string") {
			if (!VERSION_NUMBER_REGEX.test(input)) throw new DataError$1({ input }, "INVALID_VERSION", `"${input}" is not a valid version number. Version numbers must be of the format "X.Y.Z" or "vX.Y.Z", where X, Y, and Z are non-negative integers.`);
			const [major, minor, patch] = VersionNumber.formatString(input, { omitPrefix: true }).split(".").map((number) => {
				return parseIntStrict(number);
			});
			this.major = major;
			this.minor = minor;
			this.patch = patch;
		} else if (Array.isArray(input)) {
			if (input.length !== 3) throw new DataError$1({ input }, "INVALID_LENGTH", VersionNumber.NON_NEGATIVE_TUPLE_ERROR);
			const [major, minor, patch] = input.map((number) => {
				const parsedInteger = parseIntStrict(number?.toString());
				if (parsedInteger < 0) throw new DataError$1({ input }, "NEGATIVE_INPUTS", VersionNumber.NON_NEGATIVE_TUPLE_ERROR);
				return parsedInteger;
			});
			this.major = major;
			this.minor = minor;
			this.patch = patch;
		} else throw new DataError$1({ input }, "INVALID_INPUT", normaliseIndents`
        The provided input can not be parsed into a valid version number.
        Expected either a string of format X.Y.Z or vX.Y.Z, a tuple of three numbers, or another \`VersionNumber\` instance.
      `);
	}
	/**
	* Gets the current version type of the current instance of `VersionNumber`.
	*
	* @returns Either `"major"`, `"minor"`, or `"patch"`, depending on the version type.
	*/
	get type() {
		if (this.minor === 0 && this.patch === 0) return VersionType.MAJOR;
		if (this.patch === 0) return VersionType.MINOR;
		return VersionType.PATCH;
	}
	static formatString(input, options) {
		if (options?.omitPrefix) return input.startsWith("v") ? input.slice(1) : input;
		return input.startsWith("v") ? input : `v${input}`;
	}
	/**
	* Compares two given `VersionNumber` instances depending on order.
	*
	* @param first - The first version number to compare.
	* @param second - The second version number to compare.
	*
	* @returns 0 if the version numbers are equal, 1 if the first version number is bigger than the second, and -1 if the first version number is smaller than the second.
	*/
	static compare(first, second) {
		if (first.major > second.major) return 1;
		else if (first.major < second.major) return -1;
		if (first.minor > second.minor) return 1;
		else if (first.minor < second.minor) return -1;
		if (first.patch > second.patch) return 1;
		else if (first.patch < second.patch) return -1;
		return 0;
	}
	/**
	* Checks if the provided version numbers have the exact same major, minor, and patch numbers.
	*
	* @param first - The first version number to compare.
	* @param second - The second version number to compare.
	*
	* @returns `true` if the provided version numbers have exactly the same major, minor, and patch numbers, and returns `false` otherwise.
	*/
	static isEqual(first, second) {
		return VersionNumber.compare(first, second) === 0;
	}
	/**
	* Get a formatted string representation of the current version number
	*
	* @param options - Options to apply to the string formatting.
	*
	* @returns A formatted string representation of the current version number with the options applied.
	*/
	format(options) {
		let baseOutput = `${this.major}`;
		if (!options?.omitMinor) {
			baseOutput += `.${this.minor}`;
			if (!options?.omitPatch) baseOutput += `.${this.patch}`;
		}
		return VersionNumber.formatString(baseOutput, { omitPrefix: options?.omitPrefix });
	}
	/**
	* Increments the current version number by the given increment type, returning the result as a new reference in memory.
	*
	* @param incrementType - The type of increment. Can be one of the following:
	* - `"major"`: Change the major version `v1.2.3` → `v2.0.0`
	* - `"minor"`: Change the minor version `v1.2.3` → `v1.3.0`
	* - `"patch"`: Change the patch version `v1.2.3` → `v1.2.4`
	* @param incrementAmount - The amount to increment by (defaults to 1).
	*
	* @returns A new instance of `VersionNumber` with the increment applied.
	*/
	increment(incrementType, incrementAmount = 1) {
		const incrementBy = parseIntStrict(String(incrementAmount));
		try {
			switch (incrementType) {
				case "major": return new VersionNumber([
					this.major + incrementBy,
					0,
					0
				]);
				case "minor": return new VersionNumber([
					this.major,
					this.minor + incrementBy,
					0
				]);
				case "patch": return new VersionNumber([
					this.major,
					this.minor,
					this.patch + incrementBy
				]);
				default: throw incrementType;
			}
		} catch (error) {
			if (DataError$1.checkWithCode(error, "NEGATIVE_INPUTS")) throw new DataError$1({
				currentVersion: this.toString(),
				calculatedRawVersion: `v${error.data.input.join(".")}`,
				incrementAmount
			}, "NEGATIVE_VERSION", "Cannot apply this increment amount as it would lead to a negative version number.");
			else throw error;
		}
	}
	/**
	* Checks if the current `VersionNumber` instance is bigger than the input `VersionNumber`.
	*
	* @param comparator - The `VersionNumber` to compare against.
	*
	* @returns `true` if the instance is bigger than the input, and `false` otherwise
	*/
	isGreaterThan(comparator) {
		return VersionNumber.compare(this, comparator) === 1;
	}
	/**
	* Checks if the current `VersionNumber` instance is smaller than the input `VersionNumber`.
	*
	* @param comparator - The `VersionNumber` to compare against.
	*
	* @returns `true` if the instance is smaller than the input, and `false` otherwise
	*/
	isLessThan(comparator) {
		return VersionNumber.compare(this, comparator) === -1;
	}
	/**
	* Ensures that the VersionNumber behaves correctly when attempted to be coerced to a string.
	*
	* @param hint - Not used as of now, but generally used to help with numeric coercion, I think (which we most likely do not need for version numbers).
	*
	* @returns A stringified representation of the current version number, prefixed with `v`.
	*/
	[Symbol.toPrimitive](hint) {
		if (hint === "number") throw new DataError$1({ thisVersion: this.toString() }, "INVALID_COERCION", "VersionNumber cannot be coerced to a number type.");
		return this.toString();
	}
	/**
	* Ensures that the VersionNumber behaves correctly when attempted to be converted to JSON.
	*
	* @returns A stringified representation of the current version number, prefixed with `v`.
	*/
	toJSON() {
		return this.toString();
	}
	/**
	* Get a string representation of the current version number.
	*
	* @returns A stringified representation of the current version number with the prefix.
	*/
	toString() {
		const rawString = `${this.major}.${this.minor}.${this.patch}`;
		return VersionNumber.formatString(rawString, { omitPrefix: false });
	}
};
const zodVersionNumber = z.union([
	z.string(),
	z.tuple([
		z.number(),
		z.number(),
		z.number()
	]),
	z.instanceof(VersionNumber)
]).transform((rawVersionNumber) => {
	return new VersionNumber(rawVersionNumber);
});
const SortDirection = {
	DESC: "desc",
	ASC: "asc"
};
function _parseZodSchema(parsedResult, input, onError) {
	if (!parsedResult.success) {
		if (onError) {
			if (onError instanceof Error) throw onError;
			else if (typeof onError === "function") {
				const evaluatedError = onError(parsedResult.error);
				if (evaluatedError instanceof Error) throw evaluatedError;
			}
		}
		throw new DataError$1({
			input,
			issues: parsedResult.error.issues
		}, "ZOD_ERROR", `\n\n${z.prettifyError(parsedResult.error)}\n`);
	}
	return parsedResult.data;
}
/**
* An alternative function to zodSchema.parse() that can be used to strictly parse Zod schemas.
*
* NOTE: Use `parseZodSchemaAsync` if your schema includes an asynchronous function.
*
* @category Parsers
*
* @deprecated Please use `az.with(schema).parse(input)` instead.
*
* @template SchemaType - The Zod schema type.
* @template ErrorType - The type of error to throw on invalid data.
*
* @param schema - The Zod schema to use in parsing.
* @param input - The data to parse.
* @param onError - A custom error to throw on invalid data (defaults to `DataError`). May either be the error itself, or a function that returns the error or nothing. If nothing is returned, the default error is thrown instead.
*
* @throws {DataErrorCode} If the given data cannot be parsed according to the schema.
*
* @returns The parsed data from the Zod schema.
*/
function parseZodSchema(schema, input, onError) {
	return _parseZodSchema(schema.safeParse(input), input, onError);
}
/**
* An alternative function to zodSchema.parseAsync() that can be used to strictly parse asynchronous Zod schemas.
*
* @category Parsers
*
* @deprecated Please use `az.with(schema).parseAsync(input)` instead.
*
* @template SchemaType - The Zod schema type.
* @template ErrorType - The type of error to throw on invalid data.
*
* @param schema - The Zod schema to use in parsing.
* @param input - The data to parse.
* @param onError - A custom error to throw on invalid data (defaults to `DataError`). May either be the error itself, or a function that returns the error or nothing. If nothing is returned, the default error is thrown instead.
*
* @throws {DataError} If the given data cannot be parsed according to the schema.
*
* @returns The parsed data from the Zod schema.
*/
async function parseZodSchemaAsync(schema, input, onError) {
	return _parseZodSchema(await schema.safeParseAsync(input), input, onError);
}
function zodFieldWrapper(schema) {
	return z.string().trim().transform((value) => {
		return value === "" ? null : value;
	}).pipe(schema);
}
const az = {
	field: zodFieldWrapper,
	fieldNumber: () => {
		return z.coerce.number();
	},
	versionNumber: () => {
		return zodVersionNumber;
	},
	fieldDate: () => {
		return z.coerce.date();
	},
	with: (schema) => {
		return {
			parse: (input, error) => {
				return parseZodSchema(schema, input, error);
			},
			parseAsync: async (input, error) => {
				return await parseZodSchemaAsync(schema, input, error);
			}
		};
	}
};
/**
* Represents the three common software version types.
*
* @category Types
*/
const VersionType = {
	MAJOR: "major",
	MINOR: "minor",
	PATCH: "patch"
};
/**
* Returns the result of interpolating a template string when given the strings and interpolations separately.
*
* You can pass a template string directly by doing:
*
* ```
* interpolate`Template string here`;
* ```
*
* In this case, it will be functionally the same as if you just wrote the template string by itself.
*
* @category Tagged Template
*
* @template InterpolationsType - The type of the interpolations.
*
* @param strings - The strings from the template to process.
* @param interpolations - An array of all interpolations from the template.
*
* @returns A new string with the strings and interpolations from the template applied.
*/
function interpolate(strings, ...interpolations) {
	let result = "";
	for (const [string, interpolation = ""] of paralleliseArrays(strings, interpolations)) result += string + interpolation;
	return result;
}
function calculateTabSize(line, whitespaceLength) {
	const potentialWhitespacePart = line.slice(0, whitespaceLength);
	const trimmedString = line.trimStart();
	if (potentialWhitespacePart.trim() !== "") return 0;
	const tabSize = line.length - (trimmedString.length + whitespaceLength);
	return tabSize < 0 ? 0 : tabSize;
}
function getWhitespaceLength(lines) {
	const [firstNonEmptyLine] = lines.filter((line) => {
		return line.trim() !== "";
	});
	return firstNonEmptyLine.length - firstNonEmptyLine.trimStart().length;
}
function reduceLines(lines, { preserveTabs = true }) {
	const slicedLines = lines.slice(1);
	const isFirstLineEmpty = lines[0].trim() === "";
	const whitespaceLength = getWhitespaceLength(isFirstLineEmpty ? lines : slicedLines);
	return (isFirstLineEmpty ? slicedLines : lines).map((line) => {
		const tabSize = calculateTabSize(line, whitespaceLength);
		return (preserveTabs ? fillArray(() => {
			return " ";
		}, tabSize).join("") : "") + line.trimStart();
	}).join("\n");
}
/**
* Applies any options if provided, then removes any extraneous indents from a multi-line template string.
*
* You can pass a template string directly by doing:
*
* ```typescript
* normaliseIndents`Template string here
*     with a new line
*     and another new line`;
* ```
*
* You may also pass the options first, then invoke the resulting function with a template string:
*
* ```typescript
* normaliseIndents({ preserveTabs: false })`Template string here
*     with a new line
*     and another new line`;
* ```
*
* @category Tagged Template
*
* @param first - The strings from the template to process, or the options to apply.
* @param args - An array of all interpolations from the template.
*
* @returns An additional function to invoke, or a new string with the strings and interpolations from the template applied, and extraneous indents removed.
*/
function normaliseIndents(first, ...args) {
	if (isNonNullableObject(first) && !Array.isArray(first)) {
		const options = first;
		return (strings, ...interpolations) => {
			return normaliseIndents(strings, ...interpolations, options);
		};
	}
	const strings = first;
	const options = typeof args[args.length - 1] === "object" && !Array.isArray(args[args.length - 1]) ? args.pop() : {};
	return reduceLines(interpolate(strings, ...[...args]).split("\n"), options);
}
String.raw`^(?:v)?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$`;
// src/authProviders/EndToEndAuthInsertData.ts
const endToEndAuthInsertSchema = z.object({ email: z.email() });
function parseEndToEndAuthInsertData(input) {
	return az.with(endToEndAuthInsertSchema).parse(input);
}
// src/authProviders/UserAuthProvider.ts
const userAuthProviderSchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	provider: z.enum(AuthProvider),
	providerUserId: z.string(),
	createdAt: z.coerce.date()
});
function parseUserAuthProviders(input) {
	return az.with(z.array(userAuthProviderSchema)).parse(input);
}
function parseUserAuthProvider(input) {
	return az.with(userAuthProviderSchema).parse(input);
}
// src/authProviders/UserAuthProviderInsertData.ts
const userAuthProviderInsertSchema = z.object({
	id: z.uuid().optional(),
	userId: z.uuid(),
	provider: z.enum(AuthProvider),
	providerUserId: z.string()
});
function parseUserAuthProviderInsertData(input) {
	return az.with(userAuthProviderInsertSchema).parse(input);
}
// src/blogCollections/BlogCollection.ts
const blogCollectionSchema = z.object({
	id: z.uuid(),
	name: z.string().max(100),
	userId: z.uuid(),
	description: z.string().nullable(),
	createdAt: z.coerce.date()
});
function parseBlogCollection(input) {
	return az.with(blogCollectionSchema).parse(input);
}
// src/blogCollections/BlogCollectionItem.ts
const blogCollectionItemSchema = z.object({
	id: z.uuid(),
	itemNumber: z.int().positive(),
	blogId: z.uuid(),
	blogCollectionId: z.uuid(),
	createdAt: z.coerce.date()
});
function parseBlogCollectionItem(input) {
	return az.with(blogCollectionItemSchema).parse(input);
}
// src/blogCollections/BlogCollectionItemsFilter.ts
const blogCollectionItemsFilterSchema = z.object({
	pageNumber: az.fieldNumber().int().optional(),
	pageSize: az.fieldNumber().int().optional(),
	sortColumn: z.enum(["itemNumber"]).optional(),
	sortDirection: z.enum(SortDirection).optional()
});
function parseBlogCollectionItemsFilter(input, onError) {
	return az.with(blogCollectionItemsFilterSchema).parse(input, onError);
}
// src/blogCollections/BlogCollectionItemSummary.ts
const blogCollectionItemSummarySchema = z.object({
	id: z.uuid(),
	itemNumber: z.int().positive(),
	createdAt: z.coerce.date(),
	blogId: z.uuid(),
	blogTitle: z.string(),
	authorId: z.uuid(),
	authorUsername: z.string(),
	authorDisplayName: z.string().nullable(),
	authorProfilePictureUrl: z.string().nullable(),
	blogUpdatedAt: z.coerce.date(),
	blogPublishedAt: z.coerce.date().nullable(),
	blogCollectionId: z.uuid()
});
function parseBlogCollectionItemSummary(input, onError) {
	return az.with(blogCollectionItemSummarySchema).parse(input, onError);
}
// src/blogCollections/BlogCollectionItemsResponse.ts
const blogCollectionItemsResponseSchema = z.object({
	items: z.array(blogCollectionItemSummarySchema),
	count: z.int().nonnegative()
});
function parseBlogCollectionItemsResponse(input, onError) {
	return az.with(blogCollectionItemsResponseSchema).parse(input, onError);
}
// src/blogCollections/BlogCollectionOption.ts
const blogCollectionOptionSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	selected: z.boolean()
});
function parseBlogCollectionOptions(input) {
	return az.with(z.array(blogCollectionOptionSchema)).parse(input);
}
// src/blogCollections/BlogCollectionOptionsQueryString.ts
const blogCollectionOptionsQueryStringSchema = z.object({ selectedBlogId: z.uuid().optional() });
function parseBlogCollectionOptionsQueryString(input) {
	return az.with(blogCollectionOptionsQueryStringSchema).parse(input);
}
// src/blogCollections/BlogCollectionsFilter.ts
const blogCollectionsFilterSchema = z.object({
	userId: z.uuid().optional(),
	blogId: z.uuid().optional(),
	pageNumber: az.fieldNumber().int().optional(),
	pageSize: az.fieldNumber().int().optional(),
	sortColumn: z.enum(["createdAt"]).optional(),
	sortDirection: z.enum(SortDirection).optional()
});
function parseBlogCollectionsFilter(input) {
	return az.with(blogCollectionsFilterSchema).parse(input);
}
// src/blogCollections/BlogCollectionView.ts
const blogCollectionViewSchema = blogCollectionSchema.extend({
	username: z.string(),
	userDisplayName: z.string().nullable(),
	userProfilePictureUrl: z.string().nullable(),
	itemCount: z.int().nonnegative()
});
function parseBlogCollectionView(input) {
	return az.with(blogCollectionViewSchema).parse(input);
}
// src/blogCollections/BlogCollectionsResponse.ts
const blogCollectionsResponseSchema = z.object({
	blogCollections: z.array(blogCollectionViewSchema),
	count: z.int()
});
function parseBlogCollectionsResponse(input) {
	return az.with(blogCollectionsResponseSchema).parse(input);
}
// src/blogCollections/CreateBlogCollectionItemData.ts
const createBlogCollectionItemDataSchema = z.object({ blogId: z.uuid() });
function parseCreateBlogCollectionItemData(input, onError) {
	return az.with(createBlogCollectionItemDataSchema).parse(input, onError);
}
// src/blogCollections/CreateBlogCollectionData.ts
const createBlogCollectionDataSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	items: z.array(createBlogCollectionItemDataSchema).nullable().optional()
});
function parseCreateBlogCollectionData(input, onError) {
	return az.with(createBlogCollectionDataSchema).parse(input, onError);
}
// src/blogCollections/EditBlogCollectionData.ts
const editBlogCollectionDataSchema = createBlogCollectionDataSchema.omit({ items: true });
function parseEditBlogCollectionData(input, onError) {
	return az.with(editBlogCollectionDataSchema).parse(input, onError);
}
// src/blogs/BlogState.ts
const BlogState = {
	DRAFT: "draft",
	PUBLISHED: "published",
	ARCHIVED: "archived"
};
function formatBlogState(value) {
	switch (value) {
		case BlogState.ARCHIVED: return "Archived";
		case BlogState.DRAFT: return "Draft";
		case BlogState.PUBLISHED: return "Published";
		default: throw value;
	}
}
// src/blogs/Blog.ts
const blogSchema = z.object({
	id: z.uuid(),
	authorId: z.uuid(),
	state: z.enum(BlogState),
	currentRevisionId: z.uuid(),
	updatedAt: z.coerce.date(),
	publishedAt: z.coerce.date().nullable()
});
function parseBlog(input) {
	return az.with(blogSchema).parse(input);
}
function parseBlogs(input) {
	return az.with(z.array(blogSchema)).parse(input);
}
// src/blogs/BlogQueryString.ts
const blogQueryStringSchema = z.object({ revisionNumber: z.coerce.number().int().positive().optional() });
function parseBlogQueryString(input) {
	return az.with(blogQueryStringSchema).parse(input);
}
// src/blogs/BlogRevision.ts
const blogRevisionSchema = z.object({
	id: z.uuid(),
	editorId: z.uuid(),
	blogId: z.uuid("blog_id"),
	title: z.string().max(100),
	content: z.record(z.string(), z.any()),
	version: z.int().positive(),
	revisionMessage: z.string().nullable().optional(),
	createdAt: z.coerce.date()
});
function parseBlogRevision(input) {
	return az.with(blogRevisionSchema).parse(input);
}
function parseBlogRevisionHistory(input) {
	return az.with(z.array(blogRevisionSchema)).parse(input);
}
// src/blogs/BlogsFilter.ts
const blogsFilterSchema = z.object({
	authorId: z.uuid().optional(),
	state: z.enum(BlogState).optional(),
	pageNumber: az.fieldNumber().int().optional(),
	pageSize: az.fieldNumber().int().optional(),
	sortColumn: z.enum([
		"updatedAt",
		"publishedAt",
		"state",
		"title"
	]).optional(),
	sortDirection: z.enum(SortDirection).optional(),
	searchQuery: z.string().optional()
});
function parseBlogsFilter(input) {
	return az.with(blogsFilterSchema).parse(input);
}
// src/blogs/BlogStateHistoryRow.ts
const blogStateHistorySchema = z.object({
	id: z.int().positive(),
	updatedById: z.uuid(),
	blogId: z.uuid(),
	state: z.enum(BlogState),
	revisionId: z.uuid(),
	updatedAt: z.coerce.date()
});
function parseBlogStateHistoryRow(input) {
	return az.with(blogStateHistorySchema).parse(input);
}
function parseBlogStateHistory(input) {
	return az.with(z.array(blogStateHistorySchema)).parse(input);
}
// src/blogs/BlogSummary.ts
const blogSummarySchema = z.object({
	id: z.uuid(),
	authorId: z.uuid(),
	authorUsername: z.string(),
	authorDisplayName: z.string().nullable(),
	authorProfilePictureUrl: z.string().nullable(),
	updatedAt: z.coerce.date(),
	publishedAt: z.coerce.date().nullable(),
	state: z.enum(BlogState),
	title: z.string()
});
function parseBlogSummary(input) {
	return az.with(blogSummarySchema).parse(input);
}
function parseBlogSummaries(input) {
	return az.with(z.array(blogSummarySchema)).parse(input);
}
// src/blogs/BlogSummariesResponse.ts
const blogSummariesResponseSchema = z.object({
	blogs: z.array(blogSummarySchema),
	count: z.int()
});
function parseBlogSummariesResponse(input) {
	return az.with(blogSummariesResponseSchema).parse(input);
}
// src/blogs/BlogView.ts
const blogViewSchema = blogSummarySchema.extend({
	revisionNumber: z.int(),
	content: z.record(z.string(), z.any())
});
function parseBlogView(input) {
	return az.with(blogViewSchema).parse(input);
}
// src/blogs/CreateBlogData.ts
const createBlogSchema = z.strictObject({
	title: z.string(),
	content: z.record(z.string(), z.any()),
	state: z.enum(BlogState)
});
function parseCreateBlogData(input, error) {
	return az.with(createBlogSchema).parse(input, error);
}
// src/blogs/EditBlogData.ts
const blogUpdateSchema = createBlogSchema.omit({ state: true }).extend({ state: z.enum(BlogState) });
function parseEditBlogData(input) {
	return az.with(blogUpdateSchema).parse(input);
}
// src/blogs/EditBlogStateData.ts
const editBlogStateDataSchema = z.object({ state: z.enum(BlogState) });
function parseEditBlogStateData(input) {
	return az.with(editBlogStateDataSchema).parse(input);
}
// src/blogs/PutBlogToBlogCollectionsData.ts
const putBlogToBlogCollectionsSchema = z.object({ blogCollectionIds: z.array(z.uuid()) });
function parsePutBlogToBlogCollectionsData(input) {
	return az.with(putBlogToBlogCollectionsSchema).parse(input);
}
// src/metadata/Metadata.ts
const metadataSchema = z.object({ commitHash: z.string().nullable() });
function parseMetadata(input) {
	return az.with(metadataSchema).parse(input);
}
// src/users/CreateUserData.ts
const createUserSchema = z.object({
	username: z.string().max(100),
	displayName: z.string().max(50).nullable(),
	description: z.string().nullable(),
	email: z.email(),
	dateOfBirth: z.coerce.date().nullable()
});
function parseCreateUserData(input) {
	return az.with(createUserSchema).parse(input);
}
// src/users/User.ts
const userSchema = z.object({
	id: z.uuid(),
	username: z.string().max(100),
	description: z.string().nullable().optional(),
	displayName: z.string().max(50).nullable(),
	email: z.email(),
	dateOfBirth: z.coerce.date().nullable(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().nullable(),
	profilePictureFileKey: z.string().nullable(),
	profilePictureFileName: z.string().nullable()
});
function parseUser(input) {
	return az.with(userSchema).parse(input);
}
function parseUsers(input) {
	return az.with(z.array(userSchema)).parse(input);
}
// src/users/UserProfile.ts
const userProfileSchema = z.object({
	id: z.uuid(),
	username: z.string().max(100),
	description: z.string().nullable(),
	displayName: z.string().max(50).nullable(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().nullable(),
	profilePictureUrl: z.string().nullable()
});
function parseUserProfile(input) {
	return az.with(userProfileSchema).parse(input);
}
// src/users/UserProfileForm.ts
const userProfileFormSchema = z.object({
	username: az.field(z.string().max(100)),
	displayName: az.field(z.string().max(50).nullable()),
	description: az.field(z.string().nullable())
});
// src/users/UserProfileInsertData.ts
const userProfileInsertSchema = z.object({
	username: z.string().max(100),
	displayName: z.string().max(50).optional(),
	description: z.string().optional().nullable()
});
function parseUserProfileInsertData(input) {
	return az.with(userProfileInsertSchema).parse(input);
}
// src/users/UserProfileUpdateData.ts
const userProfileUpdateSchema = userProfileInsertSchema;
function parseUserProfileUpdateData(input) {
	return az.with(userProfileUpdateSchema).parse(input);
}
// src/userSessions/CreateUserSessionData.ts
const createUserSessionSchema = z.object({
	userId: z.uuid(),
	expiresAt: z.coerce.date().optional()
});
function parseCreateUserSessionData(input) {
	return az.with(createUserSessionSchema).parse(input);
}
// src/userSessions/UserSession.ts
const userSessionSchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	expiresAt: z.coerce.date(),
	createdAt: z.coerce.date()
});
function parseUserSession(input) {
	return az.with(userSessionSchema).parse(input);
}
export { AuthProvider, BlogState, blogCollectionItemSchema, blogCollectionItemSummarySchema, blogCollectionItemsFilterSchema, blogCollectionItemsResponseSchema, blogCollectionOptionSchema, blogCollectionOptionsQueryStringSchema, blogCollectionSchema, blogCollectionViewSchema, blogCollectionsFilterSchema, blogCollectionsResponseSchema, blogQueryStringSchema, createBlogCollectionDataSchema, createBlogCollectionItemDataSchema, editBlogCollectionDataSchema, editBlogStateDataSchema, formatBlogState, metadataSchema, parseBlog, parseBlogCollection, parseBlogCollectionItem, parseBlogCollectionItemSummary, parseBlogCollectionItemsFilter, parseBlogCollectionItemsResponse, parseBlogCollectionOptions, parseBlogCollectionOptionsQueryString, parseBlogCollectionView, parseBlogCollectionsFilter, parseBlogCollectionsResponse, parseBlogQueryString, parseBlogRevision, parseBlogRevisionHistory, parseBlogStateHistory, parseBlogStateHistoryRow, parseBlogSummaries, parseBlogSummariesResponse, parseBlogSummary, parseBlogView, parseBlogs, parseBlogsFilter, parseCreateBlogCollectionData, parseCreateBlogCollectionItemData, parseCreateBlogData, parseCreateUserData, parseCreateUserSessionData, parseEditBlogCollectionData, parseEditBlogData, parseEditBlogStateData, parseEndToEndAuthInsertData, parseMetadata, parsePutBlogToBlogCollectionsData, parseUser, parseUserAuthProvider, parseUserAuthProviderInsertData, parseUserAuthProviders, parseUserProfile, parseUserProfileInsertData, parseUserProfileUpdateData, parseUserSession, parseUsers, putBlogToBlogCollectionsSchema, userProfileFormSchema, userProfileSchema };
